import { useEffect, useMemo, useRef, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import {
  Bot,
  Boxes,
  Globe,
  Smartphone,
  Zap,
  Sparkles,
  Compass,
  ArrowRight,
  Loader2,
  Check,
  Megaphone,
  Search,
  MessageSquare,
  Repeat,
  Users,
  Layers,
  Star,
  ExternalLink,
  Mail,
  Phone,
} from "lucide-react";
import { toast } from "sonner";
import { API_URL } from "@/utils/api";
import { useNexusHomeContent } from "@/contexts/NexusHomeContentContext";
import { captureAttribution, getAttribution } from "@/utils/attribution";
import { getConsentLeadFields } from "@/utils/cookieConsent";
import { trackGenerateLead } from "@/utils/leadTracking";

// Map icon-name strings (stored in DB) to lucide-react components.
const ICON_MAP = {
  Bot,
  Boxes,
  Globe,
  Smartphone,
  Zap,
  Sparkles,
  Compass,
  Megaphone,
  Search,
  MessageSquare,
  Repeat,
  Users,
  Layers,
};

function getIcon(iconName, fallback = Sparkles) {
  if (iconName && ICON_MAP[iconName]) return ICON_MAP[iconName];
  return fallback;
}

// Per-logo optical size compensation for the trust logo cloud.
// Every logo shares the exact same outer box; only the inner max-height
// varies to make aspect-ratio and internal-whitespace differences feel
// visually even. Keyed by filename suffix so admins can swap files
// without touching this map.
const LOGO_SIZE_OVERRIDES = {
  "tradfallarna.png": "max-h-10 lg:max-h-12",
  "rena-tjanster.png": "max-h-12 lg:max-h-14",
  "gransbygden.png": "max-h-6 lg:max-h-8",
  "bentech.png": "max-h-6 lg:max-h-7",
  "svea-ytservice.png": "max-h-12 lg:max-h-14",
  "ic-identity.png": "max-h-8 lg:max-h-10",
  "solglantan.png": "max-h-7 lg:max-h-9",
  "aljo-bygg.png": "max-h-6 lg:max-h-8",
  "rent-och-ro.png": "max-h-11 lg:max-h-14",
};
function logoSizeClass(url) {
  if (!url) return "max-h-8 lg:max-h-9";
  const match = Object.keys(LOGO_SIZE_OVERRIDES).find((k) => url.endsWith(k));
  return match ? LOGO_SIZE_OVERRIDES[match] : "max-h-8 lg:max-h-9";
}

// ---- Inline defaults so the page still renders sanely if a stored
// nexus_home doc is missing the new sections. Backend already ships the
// same defaults via DEFAULT_NEXUS_HOME; this is the last-line fallback.
const FALLBACK = {
  hero: {
    eyebrow: "Elido: hela vägen till affären",
    title_prefix: "Vi hjälper ditt företag med",
    rotator_words: [
      { label: "hemsidor som säljer", href: "/hemsidor" },
      { label: "Google Ads", href: "/google-ads-byra" },
      { label: "Meta Ads", href: "/meta-ads-byra" },
      { label: "SEO som syns", href: "/seo-byra" },
      { label: "AI-chatbotar", href: "/ai-chatbot-hemsida" },
    ],
    subheadline:
      "Från första klick till ny kund – vi bygger hemsidan, driver trafiken och hjälper fler besökare att ta nästa steg.",
    cta_label: "Berätta vad ni vill förbättra",
    cta_href: "#kontakt",
    secondary_cta_label: "Se våra projekt",
    secondary_cta_href: "#projekt",
  },
  trust: {
    eyebrow: "Riktiga kunder. Riktiga projekt.",
    google_review_url: "https://share.google/9RTbLJTbmmTSv7A83",
    google_review_label: "Läs recensionerna på Google",
    google_rating: "5,0",
    google_rating_max: "5",
    google_review_count: 5,
    customer_logos: [
      { name: "Trädfällarna Laholm", logo_url: "/img/logos/rena-tjanster.png", url: "/case-tradfallarna-i-laholm" },
      { name: "Rena Tjänster", logo_url: "/img/logos/tradfallarna.png", url: "/case-renatjanster" },
      { name: "Gränsbygdens Köpcentrum", logo_url: "/img/logos/gransbygden.png", url: "/case-gransbygdens-chatbot" },
      { name: "Bentech", logo_url: "/img/logos/bentech.png", url: "/case-bentech-chatbot" },
      { name: "SVEA Ytservice", logo_url: "/img/logos/ic-identity.png", url: "/kundcase-svea-ytservice" },
      { name: "IC Identity", logo_url: "/img/logos/svea-ytservice.png", url: "/case-icidentity" },
      { name: "Solgläntans Redovisningsbyrå", logo_url: "/img/logos/solglantan.png", url: "/case-solglantansredovisningsbyra" },
      { name: "Aljo Bygg AB", logo_url: "/img/logos/aljo-bygg.png", url: "/case-aljobygg" },
      { name: "Rent & Ro AB", logo_url: "/img/logos/rent-och-ro.png", url: "/case-rentoro" },
    ],
    points: [
      {
        icon: "Users",
        title: "Ni pratar med David och Filip",
        description:
          "Inga säljled. Samma personer som svarar bygger, driver och följer upp arbetet.",
      },
      {
        icon: "Layers",
        title: "Alla case går att besöka",
        description:
          "Varje kund vi visar har en publik sida i drift som du kan öppna och granska själv.",
      },
      {
        icon: "Compass",
        title: "Hela kundresan ihop",
        description:
          "Trafik, hemsida, förfrågan och uppföljning. Vi ansvarar för att helheten fungerar.",
      },
    ],
  },
  services_four: {
    title: "Fyra områden. En och samma helhet.",
    subtitle: "Fokusen är alltid densamma. Fler av era besökare ska bli kunder.",
    cards: [
      {
        key: "hemsidor",
        title: "Hemsidor",
        description:
          "Snabba, tydliga hemsidor byggda för att skapa affärer. Inte bara se snygga ut.",
        icon: "Globe",
        href: "/hemsidor",
        cta_label: "Se hemsidor",
      },
      {
        key: "ads",
        title: "Google Ads & Meta Ads",
        description:
          "Trafik och kampanjer som leder rätt människor till rätt erbjudande vid rätt tillfälle.",
        icon: "Megaphone",
        href: "/google-ads-byra",
        cta_label: "Se annonsering",
      },
      {
        key: "seo",
        title: "SEO",
        description:
          "Innehåll och landningssidor byggda kring faktisk sökintention. Inte gissningar.",
        icon: "Search",
        href: "/seo-byra",
        cta_label: "Se SEO",
      },
      {
        key: "chatbot",
        title: "AI-chatbotar",
        description:
          "AI som svarar på frågor, guidar besökaren vidare och öppnar dörren till nya affärer.",
        icon: "Bot",
        href: "/ai-chatbot-hemsida",
        cta_label: "Se AI-chatbotar",
      },
    ],
  },
  journey: {
    eyebrow: "Så hänger det ihop",
    title: "Vi jobbar med hela vägen till affären.",
    subtitle:
      "Enskilda kanaler räcker sällan. Vi hjälper er få trafik, hemsida, förfrågan och uppföljning att jobba mot samma mål.",
    stages: [
      {
        icon: "Megaphone",
        title: "Trafik",
        description: "Google Ads, Meta Ads och SEO som drar rätt målgrupp till sidan.",
      },
      {
        icon: "Globe",
        title: "Hemsida",
        description: "En sida som gör det enkelt att förstå erbjudandet och ta nästa steg.",
      },
      {
        icon: "MessageSquare",
        title: "Förfrågan",
        description: "Formulär och AI-chatbot som fångar upp besökaren när intresset är som störst.",
      },
      {
        icon: "Repeat",
        title: "Uppföljning",
        description: "CRM, automation och integrationer som ser till att inget lead tappas.",
      },
    ],
  },
  projects: {
    id: "projekt",
    eyebrow: "Utvalda projekt",
    title: "Några saker vi har byggt på riktigt.",
    subtitle: "Två hemsidor och en AI-chatbot. Alla i drift, alla öppna att granska.",
    items: [
      {
        name: "Ramsberg Psykoterapi",
        category: "Hemsida · Psykoterapi & samtalsmottagning",
        description: "Företagshemsida byggd för att skapa förtroende och driva förfrågningar.",
        image_url: "/img/ny-hemsida/case-ramsberg-1280.webp",
        url: "https://ramsbergpsykoterapi.se/",
      },
      {
        name: "Rena Tjänster",
        category: "Hemsida · B2B-städning",
        description: "Företagshemsida byggd för att vinna fler företagskunder.",
        image_url: "/img/ny-hemsida/case-rena-1280.webp",
        url: "https://renatjanster.se/",
      },
      {
        name: "Gränsbygden",
        category: "AI-chatbot · Vitvaror & hemleveranser",
        description: "AI-chatbot som svarar på frågor om vitvaror och leveranser dygnet runt.",
        image_url: "/img/ai-agent-hemsida/gransbygden.png",
        url: "/case-gransbygdens-chatbot",
      },
    ],
  },
  people: {
    id: "om-oss",
    eyebrow: "Människorna bakom Elido",
    title: "Ni pratar direkt med oss som gör jobbet.",
    description:
      "Ingen säljavdelning och inga långa led. Ni når David eller Filip direkt. Samma personer som bygger, driver och följer upp.",
    members: [
      {
        name: "David Siekas",
        role: "Elido AB",
        image_url: "/img/ny-hemsida/david-siekas.png",
        phone: "072-370 81 58",
        email: "david@elido.nu",
      },
      {
        name: "Filip Imberg",
        role: "Elido AB",
        image_url: "/img/ny-hemsida/filip-imberg.jpeg",
        phone: "070-734 84 84",
        email: "filip@elido.nu",
      },
    ],
  },
  secondary: {
    eyebrow: "Behöver ni mer än en hemsida?",
    title: "System, integrationer och automation.",
    description:
      "Elido bygger även interna arbetsflöden, admin- och lagerlösningar samt integrationer mellan de verktyg ni redan använder. Det stödjer det ni gör utåt, utan att bli det ni köper i första steget.",
    cta_label: "Prata med oss om ert flöde",
    cta_href: "#kontakt",
  },
  contact: {
    title: "Vad vill ni förbättra?",
    subtitle:
      "Hemsida, annonsering, SEO, AI-chatbot eller ett smartare system. Berätta kort vad ni vill få bättre fart på så återkommer vi med ett konkret nästa steg.",
    submit_label: "Få ett förslag",
    success_message: "Tack! Vi hör av oss inom 24 timmar.",
  },
  cta: {
    title: "Redo att ta nästa steg?",
    subtitle: "Berätta vad ni vill förbättra så börjar vi där.",
    cta_label: "Kontakta oss",
    cta_href: "#kontakt",
  },
};

// ---- Utilities

function smoothScrollTo(hashHref) {
  if (!hashHref || !hashHref.startsWith("#")) return false;
  const id = hashHref.slice(1);
  const el = document.getElementById(id);
  if (!el) return false;
  el.scrollIntoView({ behavior: "smooth", block: "start" });
  if (window.history && window.history.replaceState) {
    window.history.replaceState(null, "", hashHref);
  }
  return true;
}

function CTAButton({ href, label, variant = "primary", testId, size = "md" }) {
  const isInternal = href && href.startsWith("/");
  const isHash = href && href.startsWith("#");
  const baseStyles =
    variant === "primary"
      ? "bg-[#3A8A98] hover:bg-[#2c7a87] text-white shadow-lg shadow-[#3A8A98]/25"
      : "bg-white/5 hover:bg-white/10 text-white border border-white/15";
  const sizing =
    size === "lg" ? "px-8 py-4 text-base" : "px-7 py-3.5 text-sm";
  const className = `inline-flex items-center gap-2 rounded-full font-semibold transition-all duration-300 hover:translate-y-[-1px] ${sizing} ${baseStyles}`;
  const content = (
    <>
      <span>{label}</span>
      <ArrowRight className="w-4 h-4" />
    </>
  );
  if (isHash) {
    return (
      <a
        href={href}
        className={className}
        data-testid={testId}
        onClick={(e) => {
          if (smoothScrollTo(href)) e.preventDefault();
        }}
      >
        {content}
      </a>
    );
  }
  if (isInternal) {
    return (
      <Link to={href} className={className} data-testid={testId}>
        {content}
      </Link>
    );
  }
  return (
    <a href={href || "#"} className={className} data-testid={testId}>
      {content}
    </a>
  );
}

// ---- Hero with rotating word

function HeroRotator({ words, prefersReducedMotion }) {
  const list = Array.isArray(words) && words.length > 0 ? words : FALLBACK.hero.rotator_words;
  const [idx, setIdx] = useState(0);
  const [phase, setPhase] = useState("in"); // 'in' | 'out'

  useEffect(() => {
    if (prefersReducedMotion) return; // static — no cycling
    let cancelled = false;
    const advance = () => {
      if (cancelled) return;
      setPhase("out");
      setTimeout(() => {
        if (cancelled) return;
        setIdx((i) => (i + 1) % list.length);
        setPhase("in");
      }, 380); // out duration
    };
    const t = setInterval(advance, 2600);
    return () => {
      cancelled = true;
      clearInterval(t);
    };
  }, [list.length, prefersReducedMotion]);

  const current = list[idx] || list[0];
  const isInternal = current.href && current.href.startsWith("/");
  const isHash = current.href && current.href.startsWith("#");

  // Compute widest word to reserve space (avoid CLS).
  const widthReserve = useMemo(() => {
    const longest = list.reduce((a, b) => (b.label.length > a.length ? b.label : a), "");
    return longest;
  }, [list]);

  const inner = (
    <span
      className={`inline-block will-change-transform ${
        prefersReducedMotion
          ? ""
          : phase === "in"
          ? "opacity-100 translate-y-0 transition-all duration-500 ease-out"
          : "opacity-0 -translate-y-2 transition-all duration-300 ease-in"
      }`}
      style={{ transitionProperty: "opacity, transform" }}
      data-testid="nexus-hero-rotator-word"
      key={idx}
    >
      {current.label}
    </span>
  );

  const wrapClass =
    "relative inline-flex items-baseline align-baseline underline decoration-[#3A8A98]/50 decoration-2 underline-offset-8 hover:decoration-[#5DBCCB] transition-colors";

  return (
    <span
      className="relative inline-block align-baseline"
      style={{ minWidth: "1ch" }}
      aria-live="polite"
    >
      {/* Invisible width reserver — largest word occupies width, prevents layout shift */}
      <span aria-hidden="true" className="invisible whitespace-nowrap">
        {widthReserve}
      </span>
      <span className="absolute inset-0 flex items-baseline">
        {current.href ? (
          isInternal ? (
            <Link to={current.href} className={wrapClass}>
              {inner}
            </Link>
          ) : isHash ? (
            <a
              href={current.href}
              className={wrapClass}
              onClick={(e) => {
                if (smoothScrollTo(current.href)) e.preventDefault();
              }}
            >
              {inner}
            </a>
          ) : (
            <a href={current.href} className={wrapClass}>
              {inner}
            </a>
          )
        ) : (
          <span className={wrapClass}>{inner}</span>
        )}
      </span>
    </span>
  );
}

function usePrefersReducedMotion() {
  const [reduced, setReduced] = useState(false);
  useEffect(() => {
    if (typeof window === "undefined" || !window.matchMedia) return;
    const mq = window.matchMedia("(prefers-reduced-motion: reduce)");
    const set = () => setReduced(mq.matches);
    set();
    mq.addEventListener?.("change", set);
    return () => mq.removeEventListener?.("change", set);
  }, []);
  return reduced;
}

// Subtle scroll reveal used across sections. Adds .is-visible when the
// target enters viewport; CSS handles the transition. Automatically
// no-ops for reduced-motion users.
function useReveal(options = {}) {
  const ref = useRef(null);
  const reduced = usePrefersReducedMotion();
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    if (reduced) {
      el.classList.add("is-visible");
      return;
    }
    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting) {
            el.classList.add("is-visible");
            io.unobserve(el);
          }
        });
      },
      { threshold: 0.15, rootMargin: "0px 0px -8% 0px", ...options },
    );
    io.observe(el);
    return () => io.disconnect();
  }, [reduced]);
  return ref;
}

// Small reusable "revealable" wrapper. Adds the initial + transition
// classes so parent's is-visible toggle animates the child.
function Reveal({ children, delay = 0, as: Tag = "div", className = "", ...rest }) {
  const style = delay ? { transitionDelay: `${delay}ms` } : undefined;
  return (
    <Tag
      className={`nexus-reveal ${className}`.trim()}
      style={style}
      {...rest}
    >
      {children}
    </Tag>
  );
}

function HeroSection({ data }) {
  const hero = data || FALLBACK.hero;
  const reduced = usePrefersReducedMotion();
  const revealRef = useReveal();
  const words = Array.isArray(hero.rotator_words) && hero.rotator_words.length > 0
    ? hero.rotator_words
    : FALLBACK.hero.rotator_words;

  return (
    <section
      ref={revealRef}
      className="nexus-reveal-scope relative min-h-screen flex items-center overflow-hidden bg-[#0a1418]"
      data-testid="nexus-hero"
    >
      {/* Soft ambient glow — layered radial gradients, no dashboard mockup */}
      <div
        className="absolute inset-0"
        style={{
          background:
            "radial-gradient(60% 55% at 85% 15%, rgba(58,138,152,0.28) 0%, transparent 60%), radial-gradient(45% 60% at 8% 95%, rgba(37,94,105,0.20) 0%, transparent 55%), linear-gradient(180deg, #0a1418 0%, #081016 100%)",
        }}
        aria-hidden="true"
      />
      {/* Grain overlay for premium texture */}
      <div
        className="absolute inset-0 opacity-[0.035] pointer-events-none mix-blend-overlay"
        style={{
          backgroundImage:
            "url(\"data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='140' height='140'><filter id='n'><feTurbulence baseFrequency='0.9' seed='7' /><feColorMatrix values='0 0 0 0 1 0 0 0 0 1 0 0 0 0 1 0 0 0 0.55 0'/></filter><rect width='100%' height='100%' filter='url(%23n)'/></svg>\")",
        }}
        aria-hidden="true"
      />

      <div className="relative z-10 max-w-6xl mx-auto px-6 lg:px-10 py-28 lg:py-40 w-full">
        <div className="max-w-4xl">
          {hero.eyebrow ? (
            <Reveal
              className="inline-flex items-center gap-2 text-[11px] font-semibold tracking-[0.22em] uppercase text-[#5DBCCB] mb-8 lg:mb-10"
              data-testid="nexus-hero-eyebrow"
            >
              <span className="w-6 h-px bg-[#3A8A98]/60" aria-hidden="true" />
              {hero.eyebrow}
            </Reveal>
          ) : null}
          <h1
            className="text-[42px] leading-[1.02] sm:text-6xl lg:text-[80px] lg:leading-[0.98] font-bold text-white tracking-[-0.02em]"
            data-testid="nexus-hero-title"
          >
            <Reveal as="span" className="block" delay={80}>
              {hero.title_prefix || FALLBACK.hero.title_prefix}
            </Reveal>
            <Reveal
              as="span"
              delay={200}
              className="mt-2 lg:mt-4 block text-[#5DBCCB]"
              data-testid="nexus-hero-rotator"
            >
              <HeroRotator words={words} prefersReducedMotion={reduced} />
            </Reveal>
          </h1>

          {hero.subheadline ? (
            <Reveal
              as="p"
              delay={340}
              className="mt-10 lg:mt-12 text-base sm:text-lg lg:text-xl text-white/70 leading-relaxed max-w-2xl"
              data-testid="nexus-hero-subheadline"
            >
              {hero.subheadline}
            </Reveal>
          ) : null}

          <Reveal
            delay={460}
            className="mt-12 lg:mt-14 flex flex-wrap items-center gap-3 lg:gap-4"
          >
            {hero.cta_label ? (
              <CTAButton
                href={hero.cta_href || "#kontakt"}
                label={hero.cta_label}
                variant="primary"
                size="lg"
                testId="nexus-hero-cta-primary"
              />
            ) : null}
            {hero.secondary_cta_label ? (
              <CTAButton
                href={hero.secondary_cta_href || "#projekt"}
                label={hero.secondary_cta_label}
                variant="secondary"
                size="lg"
                testId="nexus-hero-cta-secondary"
              />
            ) : null}
          </Reveal>
        </div>
      </div>

      {/* Bottom fade to seam with next section */}
      <div
        className="absolute inset-x-0 bottom-0 h-24 pointer-events-none"
        style={{
          background: "linear-gradient(180deg, transparent 0%, #081016 100%)",
        }}
        aria-hidden="true"
      />
    </section>
  );
}

// ---- Trust strip — thin horizontal proof band, integrated not card-grid

function TrustSection({ data }) {
  const trust = data || FALLBACK.trust;
  const points = Array.isArray(trust.points) ? trust.points : FALLBACK.trust.points;
  const logos = Array.isArray(trust.customer_logos) ? trust.customer_logos : [];
  const rating = trust.google_rating;
  const revealRef = useReveal();
  return (
    <section
      ref={revealRef}
      className="nexus-reveal-scope relative bg-[#081016] py-14 lg:py-20"
      data-testid="nexus-trust"
    >
      <div className="max-w-6xl mx-auto px-6 lg:px-10">
        {/* Row 1 — centered Google rating + centered logo cloud */}
        <div className="pb-10 lg:pb-14 border-b border-white/[0.06]">
          {trust.google_review_url ? (
            <div className="flex justify-center">
              <a
                href={trust.google_review_url}
                target="_blank"
                rel="noopener noreferrer"
                className="group inline-flex items-center gap-2.5"
                aria-label={`Google-betyg ${rating || ""} av 5`}
                data-testid="nexus-trust-google-reviews"
              >
                <div className="flex items-center gap-0.5" aria-hidden="true">
                  {[0, 1, 2, 3, 4].map((i) => (
                    <Star
                      key={i}
                      className="w-3.5 h-3.5 text-[#FFB84D] fill-[#FFB84D]"
                    />
                  ))}
                </div>
                {rating ? (
                  <span
                    className="text-sm font-semibold text-white/85 group-hover:text-white transition-colors"
                    data-testid="nexus-trust-rating"
                  >
                    {rating} på Google
                  </span>
                ) : null}
                <ExternalLink className="w-3 h-3 text-white/35 group-hover:text-white/60 transition-colors" />
              </a>
            </div>
          ) : null}

          {logos.length > 0 ? (
            <div
              className="flex flex-wrap justify-center gap-3 sm:gap-4 max-w-[960px] mx-auto mt-5 lg:mt-6"
              data-testid="nexus-trust-customers"
            >
              {logos.map((c, idx) => {
                const isInternal = c.url && c.url.startsWith("/");
                const isExternal = c.url && !isInternal;
                // Identical box for every logo — same size + same light bg.
                // Mobile: 2 columns (calc so 2 fit exactly with 12px gap).
                // Desktop: fixed 172px so exactly 5 fit in max-w-[960px]
                // (5 × 172 + 4 × 16 = 924 < 960); the 4-item second row
                // naturally centers via justify-center.
                const boxClass =
                  "group flex items-center justify-center rounded-lg bg-white/[0.92] hover:bg-white transition-colors w-[calc(50%-6px)] sm:w-[172px] lg:w-[176px] h-14 lg:h-[68px] px-4";
                const img = (
                  <img
                    src={c.logo_url}
                    alt={c.name}
                    loading="lazy"
                    className={
                      "max-w-full w-auto h-auto object-contain " +
                      logoSizeClass(c.logo_url)
                    }
                  />
                );
                const testId = `nexus-trust-customer-${idx}`;
                if (isInternal) {
                  return (
                    <Link
                      key={idx}
                      to={c.url}
                      className={boxClass}
                      title={c.name}
                      data-testid={testId}
                    >
                      {img}
                    </Link>
                  );
                }
                if (isExternal) {
                  return (
                    <a
                      key={idx}
                      href={c.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className={boxClass}
                      title={c.name}
                      data-testid={testId}
                    >
                      {img}
                    </a>
                  );
                }
                return (
                  <div
                    key={idx}
                    className={boxClass}
                    title={c.name}
                    data-testid={testId}
                  >
                    {img}
                  </div>
                );
              })}
            </div>
          ) : null}
        </div>

        {/* Row 2 — headline block + three integrated points */}
        <div className="pt-12 lg:pt-16 grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-14">
          <Reveal className="lg:col-span-4">
            {trust.eyebrow ? (
              <div
                className="text-[11px] font-semibold tracking-[0.22em] uppercase text-[#5DBCCB] mb-4"
                data-testid="nexus-trust-eyebrow"
              >
                {trust.eyebrow}
              </div>
            ) : null}
            <h2 className="text-3xl lg:text-4xl font-bold text-white tracking-[-0.01em] leading-[1.15]">
              Vi hittar inte på siffror. Vi visar det som redan finns.
            </h2>
          </Reveal>
          <div className="lg:col-span-8 grid grid-cols-1 sm:grid-cols-3 gap-8 lg:gap-10">
            {points.map((p, idx) => {
              const Icon = getIcon(p.icon, Sparkles);
              return (
                <Reveal
                  key={idx}
                  delay={idx * 80}
                  className="relative"
                  data-testid={`nexus-trust-point-${idx}`}
                >
                  <Icon className="w-6 h-6 text-[#5DBCCB] mb-5" strokeWidth={1.5} />
                  <h3 className="text-base font-semibold text-white mb-2 tracking-tight">
                    {p.title}
                  </h3>
                  <p className="text-sm text-white/55 leading-relaxed">
                    {p.description}
                  </p>
                </Reveal>
              );
            })}
          </div>
        </div>
      </div>
    </section>
  );
}

// ---- Four services — asymmetric bento (Hemsidor featured, others compact)

function ServicesFour({ data }) {
  const svc = data || FALLBACK.services_four;
  const cards = Array.isArray(svc.cards) && svc.cards.length > 0
    ? svc.cards
    : FALLBACK.services_four.cards;
  const revealRef = useReveal();
  return (
    <section
      id="chatbot"
      ref={revealRef}
      className="nexus-reveal-scope relative bg-[#0d1a1f] py-24 lg:py-36"
      data-testid="nexus-services-four"
    >
      <div className="max-w-6xl mx-auto px-6 lg:px-10">
        {/* Section head — left-aligned, generous negative space */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-14 mb-16 lg:mb-20 items-end">
          <Reveal className="lg:col-span-7">
            <div className="text-[11px] font-semibold tracking-[0.22em] uppercase text-[#5DBCCB] mb-4">
              Fyra huvudområden
            </div>
            <h2 className="text-3xl lg:text-5xl font-bold text-white tracking-[-0.015em] leading-[1.1]">
              {svc.title}
            </h2>
          </Reveal>
          {svc.subtitle ? (
            <Reveal delay={120} className="lg:col-span-5">
              <p className="text-base lg:text-lg text-white/60 leading-relaxed">
                {svc.subtitle}
              </p>
            </Reveal>
          ) : null}
        </div>

        {/* Asymmetric 12-col grid:
            row 1: card 0 span 8 (featured) + card 1 span 4
            row 2: card 2 span 5 + card 3 span 7 */}
        <div className="grid grid-cols-1 md:grid-cols-12 gap-4 lg:gap-6 auto-rows-fr">
          {cards.map((card, idx) => {
            const Icon = getIcon(card.icon, Sparkles);
            const isInternal = card.href && card.href.startsWith("/");
            const isHash = card.href && card.href.startsWith("#");

            // Asymmetric span pattern
            const spanMap = [
              "md:col-span-8", // 0 — Hemsidor (featured)
              "md:col-span-4", // 1 — Ads
              "md:col-span-5", // 2 — SEO
              "md:col-span-7", // 3 — AI-chatbotar
            ];
            const span = spanMap[idx] || "md:col-span-6";
            const featured = idx === 0 || idx === 3;

            const Content = (
              <article
                className={`nexus-service-card group relative h-full flex flex-col justify-between overflow-hidden bg-[#0a1418] border border-white/[0.06] rounded-3xl transition-all duration-500 hover:border-[#3A8A98]/50 hover:bg-[#0f1c22] ${
                  featured ? "p-8 lg:p-12 min-h-[260px] lg:min-h-[340px]" : "p-7 lg:p-10 min-h-[220px] lg:min-h-[280px]"
                }`}
                data-testid={`nexus-service-${card.key || idx}`}
              >
                {/* Big number */}
                <div
                  className={`absolute right-6 top-5 lg:right-10 lg:top-8 font-bold text-white/[0.04] group-hover:text-[#3A8A98]/15 transition-colors ${
                    featured ? "text-[110px] lg:text-[160px]" : "text-[80px] lg:text-[110px]"
                  } leading-none tracking-tighter select-none pointer-events-none`}
                  aria-hidden="true"
                >
                  0{idx + 1}
                </div>

                <div className="relative z-10">
                  <Icon
                    className={`text-[#5DBCCB] mb-6 lg:mb-8 ${
                      featured ? "w-8 h-8 lg:w-9 lg:h-9" : "w-6 h-6 lg:w-7 lg:h-7"
                    }`}
                    strokeWidth={1.4}
                  />
                  <h3
                    className={`font-bold text-white tracking-tight mb-3 ${
                      featured ? "text-2xl lg:text-3xl" : "text-xl lg:text-2xl"
                    }`}
                  >
                    {card.title}
                  </h3>
                  <p
                    className={`text-white/55 leading-relaxed ${
                      featured ? "text-base lg:text-lg max-w-md" : "text-sm lg:text-base max-w-sm"
                    }`}
                  >
                    {card.description}
                  </p>
                </div>

                {card.href ? (
                  <div className="relative z-10 mt-8 flex items-center gap-2 text-xs font-semibold text-[#5DBCCB] tracking-[0.14em] uppercase transition-transform duration-300 group-hover:translate-x-1">
                    <span>{card.cta_label || "Läs mer"}</span>
                    <ArrowRight className="w-3.5 h-3.5" />
                  </div>
                ) : null}
              </article>
            );

            const wrapper = (child) => (
              <Reveal key={card.key || idx} delay={idx * 60} className={`${span} h-full block`}>
                {child}
              </Reveal>
            );

            if (isInternal) {
              return wrapper(
                <Link to={card.href} className="block h-full">
                  {Content}
                </Link>,
              );
            }
            if (isHash) {
              return wrapper(
                <a
                  href={card.href}
                  onClick={(e) => {
                    if (smoothScrollTo(card.href)) e.preventDefault();
                  }}
                  className="block h-full"
                >
                  {Content}
                </a>,
              );
            }
            if (card.href) {
              return wrapper(
                <a href={card.href} className="block h-full">
                  {Content}
                </a>,
              );
            }
            return wrapper(<div className="h-full">{Content}</div>);
          })}
        </div>
      </div>
    </section>
  );
}

// ---- Journey — LIGHT warm section, connected horizontal pipeline

function JourneySection({ data }) {
  const j = data || FALLBACK.journey;
  const stages = Array.isArray(j.stages) && j.stages.length > 0 ? j.stages : FALLBACK.journey.stages;
  const revealRef = useReveal();
  return (
    <section
      id="driftsystem"
      ref={revealRef}
      className="nexus-reveal-scope relative bg-[#F4EFE4] py-24 lg:py-36 text-[#0a1418]"
      data-testid="nexus-journey"
    >
      {/* subtle top/bottom fade so it seams into dark neighbours */}
      <div
        className="absolute inset-x-0 top-0 h-12 pointer-events-none"
        style={{ background: "linear-gradient(180deg, rgba(10,20,24,0.06) 0%, transparent 100%)" }}
        aria-hidden="true"
      />
      <div className="max-w-6xl mx-auto px-6 lg:px-10">
        <div className="max-w-3xl mb-14 lg:mb-20">
          {j.eyebrow ? (
            <Reveal className="inline-flex items-center gap-2 text-[11px] font-semibold tracking-[0.22em] uppercase text-[#255E69] mb-5">
              <span className="w-6 h-px bg-[#255E69]/50" aria-hidden="true" />
              {j.eyebrow}
            </Reveal>
          ) : null}
          <Reveal
            as="h2"
            delay={80}
            className="text-3xl lg:text-5xl font-bold text-[#0a1418] tracking-[-0.015em] leading-[1.08]"
          >
            {j.title}
          </Reveal>
          {j.subtitle ? (
            <Reveal
              as="p"
              delay={180}
              className="mt-6 text-base lg:text-lg text-[#0a1418]/65 leading-relaxed max-w-2xl"
            >
              {j.subtitle}
            </Reveal>
          ) : null}
        </div>

        {/* Pipeline — horizontal on desktop, vertical timeline on mobile */}
        <div className="relative">
          {/* Desktop connector line */}
          <div
            className="hidden md:block absolute left-0 right-0 top-[38px] h-px bg-gradient-to-r from-transparent via-[#255E69]/30 to-transparent"
            aria-hidden="true"
          />
          <ol className="grid grid-cols-1 md:grid-cols-4 gap-10 md:gap-6">
            {stages.map((s, idx) => {
              const Icon = getIcon(s.icon, Sparkles);
              return (
                <Reveal
                  as="li"
                  key={idx}
                  delay={idx * 100}
                  className="relative flex md:flex-col gap-5 md:gap-0"
                  data-testid={`nexus-journey-stage-${idx}`}
                >
                  {/* Mobile timeline line + node */}
                  <div className="md:hidden flex flex-col items-center">
                    <div className="w-10 h-10 rounded-full bg-[#0a1418] text-white flex items-center justify-center">
                      <Icon className="w-4 h-4" strokeWidth={1.6} />
                    </div>
                    {idx < stages.length - 1 ? (
                      <div className="w-px flex-1 bg-[#255E69]/25 mt-2" aria-hidden="true" />
                    ) : null}
                  </div>

                  {/* Desktop node — sits on the horizontal line */}
                  <div className="hidden md:flex items-center gap-3 mb-6">
                    <div className="relative">
                      <div className="w-[76px] h-[76px] rounded-full bg-[#F4EFE4] border border-[#255E69]/25 flex items-center justify-center">
                        <div className="w-14 h-14 rounded-full bg-[#0a1418] text-white flex items-center justify-center">
                          <Icon className="w-5 h-5" strokeWidth={1.5} />
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="flex-1 md:pb-4">
                    <div className="text-[11px] font-semibold tracking-[0.22em] uppercase text-[#255E69]/70 mb-2">
                      Steg {String(idx + 1).padStart(2, "0")}
                    </div>
                    <h3 className="text-lg lg:text-xl font-bold text-[#0a1418] mb-2 tracking-tight">
                      {s.title}
                    </h3>
                    <p className="text-sm lg:text-[15px] text-[#0a1418]/65 leading-relaxed max-w-[240px]">
                      {s.description}
                    </p>
                  </div>
                </Reveal>
              );
            })}
          </ol>
        </div>
      </div>
      <div
        className="absolute inset-x-0 bottom-0 h-12 pointer-events-none"
        style={{ background: "linear-gradient(0deg, rgba(10,20,24,0.06) 0%, transparent 100%)" }}
        aria-hidden="true"
      />
    </section>
  );
}

// ---- Projects — editorial: 1 featured + 2 supporting

function ProjectsSection({ data }) {
  const p = data || FALLBACK.projects;
  const items = Array.isArray(p.items) && p.items.length > 0 ? p.items : FALLBACK.projects.items;
  const [featured, ...rest] = items;
  const revealRef = useReveal();

  const renderCta = (item, idx) => {
    if (!item.url) return null;
    if (item.url.startsWith("/")) {
      return (
        <Link
          to={item.url}
          className="inline-flex items-center gap-1.5 text-xs font-semibold text-[#5DBCCB] uppercase tracking-[0.14em] hover:text-white transition-colors"
          data-testid={`nexus-project-${idx}-link`}
        >
          Läs caset
          <ArrowRight className="w-3.5 h-3.5" />
        </Link>
      );
    }
    return (
      <a
        href={item.url}
        target="_blank"
        rel="noopener noreferrer"
        className="inline-flex items-center gap-1.5 text-xs font-semibold text-[#5DBCCB] uppercase tracking-[0.14em] hover:text-white transition-colors"
        data-testid={`nexus-project-${idx}-link`}
      >
        Besök sidan
        <ExternalLink className="w-3.5 h-3.5" />
      </a>
    );
  };

  return (
    <section
      id={p.id || "projekt"}
      ref={revealRef}
      className="nexus-reveal-scope bg-[#0a1418] py-24 lg:py-36 scroll-mt-20"
      data-testid="nexus-projects"
    >
      <div className="max-w-6xl mx-auto px-6 lg:px-10">
        {/* Head */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-14 mb-14 lg:mb-20 items-end">
          <Reveal className="lg:col-span-7">
            {p.eyebrow ? (
              <div className="text-[11px] font-semibold tracking-[0.22em] uppercase text-[#5DBCCB] mb-4">
                {p.eyebrow}
              </div>
            ) : null}
            <h2 className="text-3xl lg:text-5xl font-bold text-white tracking-[-0.015em] leading-[1.08]">
              {p.title}
            </h2>
          </Reveal>
          {p.subtitle ? (
            <Reveal delay={120} className="lg:col-span-5">
              <p className="text-base lg:text-lg text-white/60 leading-relaxed">
                {p.subtitle}
              </p>
            </Reveal>
          ) : null}
        </div>

        {/* Featured project — full-width image + right-aligned meta */}
        {featured ? (
          <Reveal
            className="mb-6 lg:mb-8"
            data-testid={`nexus-project-0`}
          >
            <div className="group grid grid-cols-1 lg:grid-cols-12 gap-6 lg:gap-10 items-stretch">
              <div className="lg:col-span-8 relative rounded-3xl overflow-hidden aspect-[16/9] lg:aspect-[16/9] bg-[#0F2A30]">
                {featured.image_url ? (
                  <img
                    src={featured.image_url}
                    alt={`${featured.name} — case av Elido`}
                    loading="eager"
                    className="w-full h-full object-cover transition-transform duration-[900ms] ease-out group-hover:scale-[1.03]"
                  />
                ) : null}
              </div>
              <div className="lg:col-span-4 flex flex-col justify-end">
                {featured.category ? (
                  <div className="text-[11px] font-semibold tracking-[0.14em] uppercase text-[#5DBCCB] mb-3">
                    {featured.category}
                  </div>
                ) : null}
                <h3 className="text-2xl lg:text-3xl font-bold text-white tracking-tight mb-3">
                  {featured.name}
                </h3>
                <p className="text-sm lg:text-base text-white/60 leading-relaxed mb-6">
                  {featured.description}
                </p>
                {renderCta(featured, 0)}
              </div>
            </div>
          </Reveal>
        ) : null}

        {/* Supporting projects — two smaller side-by-side */}
        {rest.length > 0 ? (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 lg:gap-8">
            {rest.map((item, i) => {
              const idx = i + 1;
              return (
                <Reveal
                  key={idx}
                  delay={i * 80}
                  className="group"
                  data-testid={`nexus-project-${idx}`}
                >
                  <div className="relative rounded-3xl overflow-hidden aspect-[16/10] bg-[#0F2A30] mb-5">
                    {item.image_url ? (
                      <img
                        src={item.image_url}
                        alt={`${item.name} — case av Elido`}
                        loading="lazy"
                        className="w-full h-full object-cover transition-transform duration-[900ms] ease-out group-hover:scale-[1.04]"
                      />
                    ) : null}
                  </div>
                  {item.category ? (
                    <div className="text-[11px] font-semibold tracking-[0.14em] uppercase text-[#5DBCCB] mb-2">
                      {item.category}
                    </div>
                  ) : null}
                  <h3 className="text-lg lg:text-xl font-bold text-white tracking-tight mb-2">
                    {item.name}
                  </h3>
                  <p className="text-sm text-white/55 leading-relaxed mb-4 max-w-md">
                    {item.description}
                  </p>
                  {renderCta(item, idx)}
                </Reveal>
              );
            })}
          </div>
        ) : null}
      </div>
    </section>
  );
}

// ---- People — large editorial portraits, no card borders

function PeopleSection({ data }) {
  const p = data || FALLBACK.people;
  const members = Array.isArray(p.members) && p.members.length > 0 ? p.members : FALLBACK.people.members;
  const revealRef = useReveal();
  return (
    <section
      id={p.id || "om-oss"}
      ref={revealRef}
      className="nexus-reveal-scope bg-[#081016] py-24 lg:py-36 scroll-mt-20"
      data-testid="nexus-people"
    >
      <div className="max-w-6xl mx-auto px-6 lg:px-10">
        {/* Head */}
        <div className="max-w-3xl mb-14 lg:mb-20">
          {p.eyebrow ? (
            <Reveal className="inline-flex items-center gap-2 text-[11px] font-semibold tracking-[0.22em] uppercase text-[#5DBCCB] mb-5">
              <span className="w-6 h-px bg-[#3A8A98]/60" aria-hidden="true" />
              {p.eyebrow}
            </Reveal>
          ) : null}
          <Reveal
            as="h2"
            delay={80}
            className="text-3xl lg:text-5xl font-bold text-white tracking-[-0.015em] leading-[1.08]"
          >
            {p.title}
          </Reveal>
          {p.description ? (
            <Reveal
              as="p"
              delay={180}
              className="mt-6 text-base lg:text-lg text-white/60 leading-relaxed max-w-2xl"
            >
              {p.description}
            </Reveal>
          ) : null}
        </div>

        {/* Two smaller portraits — 2 columns even on mobile so they sit side-by-side */}
        <div className="grid grid-cols-2 gap-5 sm:gap-8 lg:gap-14 max-w-3xl">
          {members.map((m, idx) => (
            <Reveal
              key={idx}
              delay={idx * 120}
              className="group"
              data-testid={`nexus-person-${idx}`}
            >
              {m.image_url ? (
                <div className="relative w-full aspect-square overflow-hidden rounded-2xl bg-[#0F2A30] mb-4 max-w-[220px]">
                  <img
                    src={m.image_url}
                    alt={m.name}
                    loading="lazy"
                    className="w-full h-full object-cover grayscale-[15%] transition-all duration-[900ms] ease-out group-hover:grayscale-0 group-hover:scale-[1.02]"
                  />
                </div>
              ) : null}
              <div className="flex flex-col gap-2">
                <div>
                  <h3 className="text-base sm:text-lg lg:text-xl font-bold text-white tracking-tight leading-tight">
                    {m.name}
                  </h3>
                  {m.role ? (
                    <div className="text-xs text-white/45 mt-0.5">{m.role}</div>
                  ) : null}
                </div>
                <div className="flex flex-col gap-1.5 pt-1 text-[12px] sm:text-sm">
                  {m.email ? (
                    <a
                      href={`mailto:${m.email}`}
                      className="inline-flex items-center gap-1.5 text-white/75 hover:text-[#5DBCCB] transition-colors break-all"
                      data-testid={`nexus-person-${idx}-email`}
                    >
                      <Mail className="w-3.5 h-3.5 flex-shrink-0" /> {m.email}
                    </a>
                  ) : null}
                  {m.phone ? (
                    <a
                      href={`tel:${m.phone.replace(/\s+/g, "")}`}
                      className="inline-flex items-center gap-1.5 text-white/75 hover:text-[#5DBCCB] transition-colors"
                      data-testid={`nexus-person-${idx}-phone`}
                    >
                      <Phone className="w-3.5 h-3.5 flex-shrink-0" /> {m.phone}
                    </a>
                  ) : null}
                </div>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}

// ---- Secondary — compact horizontal ribbon (system/automation)

function SecondarySection({ data }) {
  const s = data || FALLBACK.secondary;
  const revealRef = useReveal();
  return (
    <section
      ref={revealRef}
      className="nexus-reveal-scope bg-[#0a1418] border-t border-white/[0.06] py-14 lg:py-16"
      data-testid="nexus-secondary"
    >
      <div className="max-w-6xl mx-auto px-6 lg:px-10">
        <Reveal className="flex flex-col md:flex-row md:items-center gap-6 md:gap-10">
          <Boxes
            className="w-6 h-6 text-[#5DBCCB] flex-shrink-0"
            strokeWidth={1.5}
            aria-hidden="true"
          />
          <div className="flex-1 min-w-0">
            {s.eyebrow ? (
              <div className="text-[11px] font-semibold tracking-[0.22em] uppercase text-white/40 mb-1.5">
                {s.eyebrow}
              </div>
            ) : null}
            <div className="text-base lg:text-lg text-white/85 leading-snug">
              <span className="font-semibold text-white">{s.title}</span>
              {s.description ? (
                <span className="text-white/55"> {s.description}</span>
              ) : null}
            </div>
          </div>
          {s.cta_label ? (
            <a
              href={s.cta_href || "#kontakt"}
              onClick={(e) => {
                if ((s.cta_href || "#kontakt").startsWith("#")) {
                  if (smoothScrollTo(s.cta_href || "#kontakt")) e.preventDefault();
                }
              }}
              className="inline-flex items-center gap-2 text-sm font-semibold text-[#5DBCCB] hover:text-white transition-colors flex-shrink-0 group"
              data-testid="nexus-secondary-cta"
            >
              {s.cta_label}
              <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-1" />
            </a>
          ) : null}
        </Reveal>
      </div>
    </section>
  );
}

// ---- Contact form (unchanged logic — preserves attribution & backend)

function FormField({ label, value, onChange, placeholder, type = "text", required, testId }) {
  const inputClass =
    "w-full px-0 py-3 bg-transparent border-b border-white/15 text-white placeholder-white/25 text-base focus:outline-none focus:border-[#5DBCCB] transition-colors";
  return (
    <div>
      <label className="block text-[11px] font-semibold text-white/45 mb-3 uppercase tracking-[0.16em]">
        {label} {required ? <span className="text-[#5DBCCB]">*</span> : null}
      </label>
      {type === "textarea" ? (
        <textarea
          rows={4}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder={placeholder}
          required={required}
          className={`${inputClass} resize-y`}
          data-testid={testId}
        />
      ) : (
        <input
          type={type}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder={placeholder}
          required={required}
          className={inputClass}
          data-testid={testId}
        />
      )}
    </div>
  );
}

function ContactSection({ data }) {
  const c = data || FALLBACK.contact;
  const [form, setForm] = useState({ name: "", company: "", email: "", message: "" });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  useEffect(() => {
    captureAttribution();
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim() || !form.message.trim()) {
      toast.error("Fyll i namn, e-post och meddelande");
      return;
    }
    setSubmitting(true);
    try {
      const res = await fetch(`${API_URL}/api/contact`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ ...form, attribution: getAttribution(), ...getConsentLeadFields() }),
      });
      const result = await res.json();
      if (!res.ok || result?.success === false) {
        throw new Error(result?.error || "Kunde inte skicka");
      }
      trackGenerateLead({
        accepted: result?.accepted,
        lead_id: result?.lead_id,
        form_source: "nexus_home_contact",
      });
      setSubmitted(true);
      toast.success(c.success_message || "Tack! Vi hör av oss inom 24 timmar.");
      setForm({ name: "", company: "", email: "", message: "" });
    } catch (err) {
      toast.error(err.message || "Något gick fel");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section
      id="kontakt"
      className="bg-[#050a0d] py-24 lg:py-36 scroll-mt-20"
      data-testid="nexus-contact"
    >
      <div className="max-w-2xl mx-auto px-6 lg:px-10">
        <div className="mb-12 lg:mb-14">
          <div className="text-[11px] font-semibold tracking-[0.22em] uppercase text-[#5DBCCB] mb-4">
            Kontakt
          </div>
          <h2 className="text-3xl lg:text-5xl font-bold text-white tracking-[-0.015em] leading-[1.08]">
            {c.title || "Hör av dig"}
          </h2>
          {c.subtitle ? (
            <p className="mt-5 text-base lg:text-lg text-white/60 leading-relaxed max-w-xl">
              {c.subtitle}
            </p>
          ) : null}
        </div>

        {submitted ? (
          <div
            className="flex items-start gap-5 border-l-2 border-[#3A8A98] pl-6 py-4"
            data-testid="nexus-contact-success"
          >
            <div className="w-11 h-11 rounded-full bg-[#3A8A98]/20 border border-[#3A8A98]/40 flex items-center justify-center flex-shrink-0">
              <Check className="w-5 h-5 text-[#5DBCCB]" />
            </div>
            <div>
              <h3 className="text-xl font-semibold text-white mb-1">Tack!</h3>
              <p className="text-white/65 text-sm">
                {c.success_message || "Vi hör av oss inom 24 timmar."}
              </p>
            </div>
          </div>
        ) : (
          <form
            onSubmit={submit}
            className="space-y-6 lg:space-y-7"
            data-testid="nexus-contact-form"
          >
            <div className="grid grid-cols-1 md:grid-cols-2 gap-5 lg:gap-6">
              <FormField
                label="Namn"
                value={form.name}
                onChange={(v) => setForm({ ...form, name: v })}
                placeholder="Ditt namn"
                required
                testId="nexus-contact-name"
              />
              <FormField
                label="Företag"
                value={form.company}
                onChange={(v) => setForm({ ...form, company: v })}
                placeholder="(valfritt)"
                testId="nexus-contact-company"
              />
            </div>
            <FormField
              label="E-post"
              type="email"
              value={form.email}
              onChange={(v) => setForm({ ...form, email: v })}
              placeholder="namn@foretag.se"
              required
              testId="nexus-contact-email"
            />
            <FormField
              label="Vad vill ni få hjälp med?"
              type="textarea"
              value={form.message}
              onChange={(v) => setForm({ ...form, message: v })}
              placeholder="T.ex. fler förfrågningar, ny hemsida eller automatisera ett arbetsflöde..."
              required
              testId="nexus-contact-message"
            />
            <div className="pt-2">
              <button
                type="submit"
                disabled={submitting}
                className="w-full sm:w-auto inline-flex items-center justify-center gap-2 px-8 py-4 rounded-full bg-[#3A8A98] hover:bg-[#2c7a87] text-white font-semibold text-sm transition-all duration-300 hover:translate-y-[-1px] shadow-lg shadow-[#3A8A98]/20 disabled:opacity-60"
                data-testid="nexus-contact-submit"
              >
                {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <ArrowRight className="w-4 h-4" />}
                {c.submit_label || "Skicka meddelande"}
              </button>
            </div>
          </form>
        )}
      </div>
    </section>
  );
}

function CTASection({ data }) {
  const c = data || FALLBACK.cta;
  if (!c) return null;
  return (
    <section
      className="bg-[#0a1418] py-24 lg:py-28 border-t border-white/5"
      data-testid="nexus-cta"
    >
      <div className="max-w-3xl mx-auto px-6 lg:px-10 text-center">
        <h2 className="text-3xl lg:text-4xl font-bold text-white tracking-tight">
          {c.title}
        </h2>
        {c.subtitle ? (
          <p className="mt-5 text-base text-white/60 leading-relaxed">{c.subtitle}</p>
        ) : null}
        {c.cta_label ? (
          <div className="mt-9">
            <CTAButton
              href={c.cta_href || "#kontakt"}
              label={c.cta_label}
              variant="primary"
              size="lg"
              testId="nexus-cta-primary"
            />
          </div>
        ) : null}
      </div>
    </section>
  );
}

// ---- Page

export default function NexusHomePage() {
  // The layout shares one authoritative CMS payload with NexusHeader.
  // Never paint the old FALLBACK hero while a current payload is loading.
  const { content: cmsContent, status } = useNexusHomeContent();
  const content = status === "error" ? FALLBACK : cmsContent;
  const location = useLocation();

  // Scroll to anchor when hash is present (handles /#kontakt style deep links)
  useEffect(() => {
    const hash = location.hash;
    if (!hash || hash.length < 2) return;
    const id = hash.slice(1);
    let attempts = 0;
    let cancelled = false;
    const tryScroll = () => {
      if (cancelled) return;
      const el = document.getElementById(id);
      if (el) {
        el.scrollIntoView({ behavior: "smooth", block: "start" });
        return;
      }
      attempts += 1;
      if (attempts < 15) setTimeout(tryScroll, 100);
    };
    const t = setTimeout(tryScroll, 60);
    return () => {
      cancelled = true;
      clearTimeout(t);
    };
  }, [location.hash, location.key]);

  return (
    <div data-testid="nexus-home-page">
      <Helmet>
        <title>Elido – Hemsidor, Google Ads, SEO och AI-chatbotar för företag</title>
        <meta
          name="description"
          content="Elido hjälper företag med hela den digitala kundresan: hemsidor som säljer, Google Ads och Meta Ads, SEO och AI-chatbotar. Från trafik till uppföljning."
        />
        <meta
          property="og:title"
          content="Elido – Hemsidor, Google Ads, SEO och AI-chatbotar för företag"
        />
        <meta
          property="og:description"
          content="Från första klick till ny kund — vi bygger hemsidan, driver trafiken och hjälper fler besökare att ta nästa steg."
        />
        <meta property="og:url" content="https://elido.nu/" />
        <meta property="og:type" content="website" />
        <link rel="canonical" href="https://elido.nu/" />
      </Helmet>
      {content ? (
        <>
      <HeroSection data={content?.hero} />
      <TrustSection data={content?.trust} />
      <ServicesFour data={content?.services_four} />
      <JourneySection data={content?.journey} />
      <ProjectsSection data={content?.projects} />
      <PeopleSection data={content?.people} />
      <SecondarySection data={content?.secondary} />
      <ContactSection data={content?.contact} />
        </>
      ) : (
        <div
          className="min-h-screen bg-[#0a1418]"
          role="status"
          aria-label="Laddar startsidan"
          data-testid="nexus-home-loading"
        />
      )}
    </div>
  );
}
